import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { SECTORS, Sector, track } from "./navConfig";
import { Micro, FOCUS, REGION, delay } from "./primitives";
import { NavMenuAd } from "@/components/AdSlot";

const RailSpark = ({ daily }: { daily: { date: string; count: number }[] }) => {
  if (!daily || daily.length < 2) return null;
  const counts = daily.map((d) => d.count);
  const max = Math.max(1, ...counts);
  const W = 120, H = 28;
  const pts = counts.map((c, i) => [(i / (counts.length - 1)) * W, H - 4 - (c / max) * (H - 8)]);
  const line = pts.map((p) => p.join(",")).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-[120px] h-7 shrink-0" aria-hidden>
      <defs>
        <linearGradient id="nl-rail-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#7C3AED" /><stop offset="100%" stopColor="#00E5FF" />
        </linearGradient>
      </defs>
      <polyline pathLength={1} className="nl-spark-line" points={line} fill="none"
        stroke="url(#nl-rail-line)" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2" fill="#00E5FF" />
    </svg>
  );
};

const SectorCard = ({ s, trending, wide }: { s: Sector; trending: boolean; wide: boolean }) => (
  <Link to={s.to} data-testid={`sector-card-${s.slug}`}
    onClick={() => track("nav_click", { sector: s.slug })}
    className={`group/sec relative overflow-hidden rounded-xl p-3.5 min-h-[96px] flex flex-col justify-end
      transition-colors duration-200 ${FOCUS} ${wide ? "col-span-6 lg:col-span-4" : "col-span-6 lg:col-span-3"}
      ${trending
        ? "border border-[#00E5FF]/35 bg-[#0d1016] shadow-[0_0_28px_-10px_rgba(0,229,255,0.35)] hover:border-[#00E5FF]/60"
        : "border border-white/10 bg-[#0d0e14] hover:border-[#7C3AED]/50 hover:bg-[#12131d]"}`}>
    <img src={`/sectors/${s.slug}.jpg`} alt="" aria-hidden loading="lazy"
      className={`absolute inset-y-0 right-0 h-full w-[58%] object-cover mix-blend-screen motion-safe:transition-all motion-safe:duration-300 motion-safe:group-hover/sec:scale-[1.05] ${
        trending ? "opacity-75" : "opacity-55 group-hover/sec:opacity-80"}`}
      style={{ maskImage: "linear-gradient(to right, transparent 0%, black 45%)", WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 45%)" }} />
    {trending && (
      <span data-testid="sector-trending-badge"
        className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 font-mono text-[8px] font-bold uppercase tracking-widest text-[#00E5FF] border border-[#00E5FF]/40 bg-black/60 rounded px-1.5 py-0.5">
        Trending <TrendingUp className="h-2.5 w-2.5" />
      </span>
    )}
    <span className="relative flex items-center gap-1.5 text-sm font-bold text-white">
      {s.label}
      <ArrowRight aria-hidden className="h-3 w-3 text-[#00E5FF] opacity-0 -translate-x-1 group-hover/sec:opacity-100 group-hover/sec:translate-x-0 motion-safe:transition-all motion-safe:duration-200" />
    </span>
    <span className="relative text-[11px] text-gray-500 leading-snug mt-0.5 max-w-[72%] line-clamp-2 group-hover/sec:text-gray-400 transition-colors">
      {s.desc}
    </span>
  </Link>
);

export const SectorsMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: activity } = useQuery({
    queryKey: ["nav-sectors-activity"],
    queryFn: async () => (await api.get("/sectors/activity")).data,
    staleTime: 5 * 60 * 1000,
  });

  const top = activity?.top;
  const topSector = top ? SECTORS.find((s) => s.slug === top.slug) : null;

  const goCoverage = (e: React.MouseEvent) => {
    e.preventDefault();
    track("nav_click", { to: "/#coverage" });
    const scroll = (tries = 0) => {
      const el = document.getElementById("coverage");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else if (tries < 10) setTimeout(() => scroll(tries + 1), 300);
    };
    if (location.pathname === "/") scroll();
    else { navigate("/"); setTimeout(() => scroll(), 350); }
  };

  return (
    <div data-testid="layernav-sectors" className="relative p-7">
      <span aria-hidden
        className="pointer-events-none select-none absolute -right-8 -bottom-12 font-heading font-black uppercase text-[110px] leading-none tracking-tighter text-white/[0.013]">
        Sectors
      </span>
      <div className={REGION} style={delay(0)}>
        <div className="flex items-center justify-between mb-4">
          <Micro cyan>Explore Sectors</Micro>
          <a href="/#coverage" onClick={goCoverage} data-testid="sectors-view-all"
            className={`group/cta inline-flex items-center gap-1.5 text-[13px] font-bold text-[#00E5FF] hover:text-white transition-colors rounded ${FOCUS}`}>
            Explore all coverage
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover/cta:translate-x-1" />
          </a>
        </div>
        <div className="grid grid-cols-12 gap-3">
          {SECTORS.map((s) => (
            <SectorCard key={s.slug} s={s} trending={top?.slug === s.slug} wide />
          ))}
        </div>
      </div>

      {topSector && (
        <Link to={topSector.to} data-testid="sectors-trending-rail"
          onClick={() => track("nav_click", { trending_sector: topSector.slug })}
          className={`group/rail mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-2.5
            hover:border-[#7C3AED]/45 hover:bg-white/[0.04] transition-colors duration-200 ${REGION} ${FOCUS}`}
          style={delay(1)}>
          <span className="flex items-center gap-3 shrink-0">
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A78BFA]">Trending Sector</span>
            <span className="flex items-center gap-1.5 font-heading font-bold text-white text-[17px]">
              {topSector.label}
              <TrendingUp className="h-4 w-4 text-[#22C55E]" />
            </span>
          </span>
          <span className="font-mono text-[11px] text-gray-600" data-testid="sectors-rail-metric">
            {top.count} {top.count === 1 ? "story" : "stories"}
            {activity?.top_share_pct ? ` · ${activity.top_share_pct}% of 24h coverage` : ""}
          </span>
          <span className="ml-auto flex items-center gap-5">
            <RailSpark daily={activity?.top_daily || []} />
            <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#00E5FF] whitespace-nowrap">
              Explore {topSector.label}
              <ArrowRight className="h-3.5 w-3.5 motion-safe:transition-transform motion-safe:duration-200 group-hover/rail:translate-x-1" />
            </span>
          </span>
        </Link>
      )}
      <NavMenuAd />
    </div>
  );
};
